import { Link, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import logo from '@/assets/logo.png';
import NavLink from './NavLink';
import Tooltip from './Tooltip';
import NotificationBell from './NotificationBell';
import { getMixedEvents, type MixedEventsPayload } from '@/hooks/useEvents';
import { getUserFromClerkId } from '@/hooks/useUser';
import { FaHome, FaBook, FaTools, FaLifeRing } from 'react-icons/fa';
import { FiMessageSquare } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useUnreadMessageCount } from '@/hooks/useMySupportRequests';
import { useAppSearch, type SearchResult } from '@/hooks/useAppSearch';
import AvatarDisplay from './AvatarDisplay';

const Nav = () => {
  const { user: clerkUser } = useUser();
  const search = useSearch({ strict: false }) as { invitationcode?: string; __clerk_ticket?: string };
  const { data: mixedData, isLoading: loadingMixed } = getMixedEvents();
  const userQuery = getUserFromClerkId(clerkUser?.id || '', { enabled: !!clerkUser?.id });

  const { data: unreadCount } = useUnreadMessageCount();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isResultsHover, setIsResultsHover] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isSearchOpen) searchInputRef.current?.focus();
  }, [isSearchOpen]);

  // Close the mobile menu on Escape
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const showSuggestions = isSearchOpen && debouncedQuery.length > 0;
  const suggestionsQuery = useAppSearch({ q: debouncedQuery, limit: 5 });
  const suggestions: SearchResult[] = Array.isArray(suggestionsQuery.data) ? (suggestionsQuery.data as SearchResult[]) : [];
  const exp = Math.max(0, userQuery.data?.exp || 0);
  const currentLevel = Math.floor(exp / 100) + 1;
  const displayName = (
    (userQuery.data?.name && userQuery.data.name.trim()) ||
    (clerkUser?.firstName ? `${clerkUser.firstName}${clerkUser?.lastName ? ` ${clerkUser.lastName}` : ''}` : '') ||
    clerkUser?.username ||
    clerkUser?.primaryEmailAddress?.emailAddress ||
    'Account'
  );

  const searchResultHref = (item: SearchResult): string | null => {
    switch (item.type) {
      case 'ARTICLE': return `/articles/${item.slug || item.id}`;
      case 'SCAM': return `/scam-library/${item.slug || item.id}`;
      case 'COURSE': return `/courses/${item.id}`;
      case 'LESSON': return `/lessons/${item.id}`;
      case 'DAILY_TIP':
      case 'SCAM_OF_THE_DAY': return `/`;
      case 'GLOBAL_EVENT': return `/footprint`;
      default: return null;
    }
  };

  const SearchResultItem = ({ item, onClose }: { item: SearchResult; onClose: () => void }) => {
    const href = searchResultHref(item);
    const thumbnail = item.image ? (
      <img src={item.image} alt={item.title} className="w-9 h-9 rounded-md object-cover flex-shrink-0 border border-slate-200 bg-slate-50" loading="lazy" />
    ) : (
      <div className="w-9 h-9 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 flex-shrink-0">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M4 4h16v16H4z" opacity="0.08" />
          <path d="M8 7h8v2H8V7zm0 4h8v2H8v-2zm0 4h5v2H8v-2z" />
        </svg>
      </div>
    );
    const content = (
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-medium text-slate-900 truncate">{item.title}</span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-violet-50 text-violet-600 whitespace-nowrap">
            {item.type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase())}
          </span>
        </div>
        {item.description && <div className="text-xs text-slate-500 line-clamp-1 mt-0.5">{item.description}</div>}
      </div>
    );

    if (href) {
      return (
        <a href={href} className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-violet-50 transition" onClick={onClose}>
          {thumbnail}
          {content}
        </a>
      );
    }
    return (
      <div className="flex items-center gap-2.5 px-3 py-2.5 opacity-70">
        {thumbnail}
        {content}
      </div>
    );
  };

  const SearchResults = ({ onClose }: { onClose: () => void }) => (
    <>
      {suggestionsQuery.isLoading ? (
        <div className="p-4 text-sm text-slate-500">Searching...</div>
      ) : suggestionsQuery.isError ? (
        <div className="p-4 text-sm text-red-600">Unable to load results.</div>
      ) : suggestions.length === 0 ? (
        <div className="p-4 text-sm text-slate-500">No results.</div>
      ) : (
        <ul className="max-h-80 overflow-auto divide-y divide-slate-100">
          {suggestions.map(item => (
            <li key={`${item.type}:${item.id}`}>
              <SearchResultItem item={item} onClose={onClose} />
            </li>
          ))}
          <li>
            <a
              href={`/search?q=${encodeURIComponent(debouncedQuery)}`}
              className="block text-center text-sm font-medium text-violet-600 hover:bg-violet-50 py-2.5"
              onClick={onClose}
            >
              See all results
            </a>
          </li>
        </ul>
      )}
    </>
  );

  return (
    <>
      <header className="w-full bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 sticky top-0 z-50 border-b border-slate-200/70 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div className="relative mx-auto flex min-h-[56px] w-full max-w-6xl items-center justify-between gap-2 px-4 py-2 md:min-h-[64px] md:px-5 md:py-2.5 lg:gap-3 lg:px-6 lg:py-3">
          {/* Logo */}
          <div className="flex items-center gap-3 md:gap-4 lg:gap-8">
            <Link to="/" search={{ invitationcode: search.invitationcode, __clerk_ticket: undefined }} className="flex shrink-0 items-center">
              <img src={logo} alt="GranGuard Logo" className="h-7 w-auto object-contain md:h-8 lg:h-9" />
            </Link>
            <nav className="hidden md:block">
              <ul className="flex items-center gap-0.5 lg:gap-1">
                <li><NavLink to="/">Home</NavLink></li>
                <li><NavLink to="/learn">Learn</NavLink></li>
                <li><NavLink to="/tools">Tools</NavLink></li>
                <li><NavLink to="/help">Support</NavLink></li>
              </ul>
            </nav>
          </div>

          {/* Desktop right actions */}
          <div className="hidden md:flex items-center justify-end gap-1.5 lg:gap-3">
            {/* Search */}
            <form
              onSubmit={e => e.preventDefault()}
              className={`relative flex items-center rounded-full transition-all duration-200 ${
                isSearchOpen
                  ? 'w-48 border border-slate-200 bg-white py-1 pl-2.5 pr-2 shadow-sm lg:w-56'
                  : 'bg-transparent border border-transparent w-10'
              }`}
            >
              <button
                type="button"
                aria-label={isSearchOpen ? 'Close search' : 'Open search'}
                onClick={() => setIsSearchOpen(o => !o)}
                className="cursor-pointer rounded-full p-2 text-slate-600 transition-colors hover:text-slate-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
              <input
                ref={searchInputRef}
                type="text"
                aria-label="Search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className={`ml-1.5 outline-none bg-transparent text-sm text-slate-900 placeholder-slate-400 transition-all duration-200 ${
                  isSearchOpen ? 'w-full opacity-100' : 'w-0 opacity-0'
                }`}
                onBlur={() => { if (!searchQuery && !isResultsHover) setIsSearchOpen(false); }}
                onKeyDown={e => { if (e.key === 'Escape') setIsSearchOpen(false); }}
              />
              {showSuggestions && (
                <div
                  ref={resultsRef}
                  className="absolute top-full mt-2 right-0 w-80 md:w-96 bg-white border border-slate-200 shadow-lg rounded-xl z-50 overflow-hidden"
                  onMouseEnter={() => setIsResultsHover(true)}
                  onMouseLeave={() => setIsResultsHover(false)}
                >
                  <SearchResults onClose={() => setIsSearchOpen(false)} />
                </div>
              )}
            </form>

            <Tooltip content="Messages" position="bottom">
              <a
                href="/messages"
                aria-label="Messages"
                className="relative inline-flex items-center justify-center rounded-full p-2 text-slate-600 transition-colors hover:text-slate-800 hover:bg-slate-100"
              >
                <FiMessageSquare className="h-6 w-6 -scale-x-100" />
                <AnimatePresence>
                  {(unreadCount ?? 0) > 0 && (
                    <motion.span
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                      className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
                    >
                      {unreadCount}
                    </motion.span>
                  )}
                </AnimatePresence>
              </a>
            </Tooltip>

            <Tooltip content="Notifications" position="bottom">
              <NotificationBell
                mixedEvents={mixedData as MixedEventsPayload}
                loading={loadingMixed}
                onEventClick={event => { if (event.link) window.open(event.link, '_blank'); }}
              />
            </Tooltip>

            <div className="h-5 w-px bg-slate-200" aria-hidden="true" />

            <Link id="account-nav-anchor" to="/account" search={{ invitationcode: search.invitationcode, __clerk_ticket: undefined }}>
              <div className="group flex min-w-0 items-center gap-1.5 rounded-xl px-0.5 py-1.5 whitespace-nowrap transition-colors hover:bg-slate-50 lg:gap-2.5 lg:px-2.5">
                <AvatarDisplay
                  email={clerkUser?.primaryEmailAddress?.emailAddress || ''}
                  avatarConfig={userQuery.data?.avatarConfig}
                  imageUrl={clerkUser?.imageUrl}
                  size={32}
                  className="border border-slate-200"
                />
                <div className="hidden min-w-0 flex-col leading-tight lg:flex">
                  <span className="text-sm font-medium block truncate text-slate-900">{displayName}</span>
                  <span className="text-sm text-slate-600 block truncate">Lvl {currentLevel} · {exp} pts</span>
                </div>
              </div>
            </Link>
          </div>

          {/* Mobile actions -- account text stays smaller since it's secondary info */}
          <div className="flex items-center gap-1 md:hidden">
            <button
              type="button"
              aria-label={isSearchOpen ? 'Close search' : 'Open search'}
              onClick={() => setIsSearchOpen(o => !o)}
              className="inline-flex items-center justify-center rounded-md p-2 text-slate-600 hover:bg-slate-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>
            <a
              href="/messages"
              className="relative inline-flex items-center justify-center rounded-md p-2 text-slate-600 hover:bg-slate-100"
              aria-label="Messages"
            >
              <FiMessageSquare className="h-6 w-6 -scale-x-100" />
              <AnimatePresence>
                {(unreadCount ?? 0) > 0 && (
                  <motion.span
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                    className="absolute top-1 right-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-red-500 px-0.5 text-[9px] font-bold text-white"
                  >
                    {unreadCount}
                  </motion.span>
                )}
              </AnimatePresence>
            </a>
            <NotificationBell
              mixedEvents={mixedData as MixedEventsPayload}
              loading={loadingMixed}
              onEventClick={event => { if (event.link) window.open(event.link, '_blank'); }}
            />
            <button
              type="button"
              aria-label="Toggle navigation menu"
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(o => !o)}
              className="inline-flex items-center justify-center rounded-md p-2 text-slate-600 hover:bg-slate-100"
            >
              {isMobileMenuOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile search popover */}
          {isSearchOpen && (
            <div className="absolute left-4 right-4 top-[calc(100%+8px)] z-50 md:hidden">
              <div
                className="bg-white border border-slate-200 rounded-xl shadow-lg p-2"
                onMouseEnter={() => setIsResultsHover(true)}
                onMouseLeave={() => setIsResultsHover(false)}
              >
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M11 4a7 7 0 105.292 12.292l3.707 3.707 1.414-1.414-3.707-3.707A7 7 0 0011 4zm0 2a5 5 0 110 10 5 5 0 010-10z" fill="currentColor"/>
                  </svg>
                  <input
                    ref={searchInputRef}
                    type="text"
                    aria-label="Search"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full rounded-lg border border-slate-200 pl-9 pr-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500 placeholder-slate-400"
                    onKeyDown={e => { if (e.key === 'Escape') setIsSearchOpen(false); }}
                    onBlur={() => { if (!searchQuery && !isResultsHover) setIsSearchOpen(false); }}
                  />
                </div>
                {debouncedQuery && (
                  <div className="mt-2">
                    <SearchResults onClose={() => setIsSearchOpen(false)} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Mobile menu overlay */}
      <div
        inert={!isMobileMenuOpen}
        className={`fixed inset-0 z-40 md:hidden ${isMobileMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          className={`absolute right-0 top-0 h-full w-[65%] max-w-[280px] bg-white shadow-xl transform transition-transform duration-300 ease-out ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <span className="font-semibold text-base text-slate-900">Menu</span>
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setIsMobileMenuOpen(false)}
              className="rounded-md p-1.5 hover:bg-slate-100 text-slate-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Mobile profile */}
          <Link to="/account" search={{ invitationcode: search.invitationcode, __clerk_ticket: undefined }} onClick={() => setIsMobileMenuOpen(false)}>
            <div className="flex items-center gap-3 p-4 border-b border-slate-100 hover:bg-slate-50 transition-colors">
              <AvatarDisplay
                email={clerkUser?.primaryEmailAddress?.emailAddress || ''}
                avatarConfig={userQuery.data?.avatarConfig}
                imageUrl={clerkUser?.imageUrl}
                size={44}
                className="border border-slate-200"
              />
              <div className="flex flex-col leading-tight min-w-0">
                <span className="text-sm font-medium text-slate-900 truncate">{displayName}</span>
                <span className="text-sm text-slate-600">Lvl {currentLevel} · {exp} pts</span>
              </div>
            </div>
          </Link>

          {/* Mobile nav links */}
          <nav className="p-3">
            <ul className="space-y-1">
              <li onClick={() => setIsMobileMenuOpen(false)}>
                <NavLink to="/" variant="mobile">
                  <span className="inline-flex items-center gap-2.5">
                    <FaHome size={16} className="text-slate-400" />
                    <span>Home</span>
                  </span>
                </NavLink>
              </li>
              <li onClick={() => setIsMobileMenuOpen(false)}>
                <NavLink to="/learn" variant="mobile">
                  <span className="inline-flex items-center gap-2.5">
                    <FaBook size={16} className="text-slate-400" />
                    <span>Learn</span>
                  </span>
                </NavLink>
              </li>
              <li onClick={() => setIsMobileMenuOpen(false)}>
                <NavLink to="/tools" variant="mobile">
                  <span className="inline-flex items-center gap-2.5">
                    <FaTools size={16} className="text-slate-400" />
                    <span>Tools</span>
                  </span>
                </NavLink>
              </li>
              <li onClick={() => setIsMobileMenuOpen(false)}>
                <NavLink to="/help" variant="mobile">
                  <span className="inline-flex items-center gap-2.5">
                    <FaLifeRing size={16} className="text-slate-400" />
                    <span>Support</span>
                  </span>
                </NavLink>
              </li>
            </ul>
          </nav>
        </div>
      </div>
      {/* NewEventToasts is mounted globally in __root.tsx so it also renders
          on full-screen routes (slide wizards) where Nav is hidden. */}
    </>
  );
};

export default Nav;
